import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { ResumeService } from 'src/services/api/ResumeService';
import { UserService } from 'src/services/api/UserService';
import {
  setToken,
  setRefreshToken,
  setUser,
  getUser,
} from 'src/utils/authStorage';

export const useGenericOTP = (
  value,
  type,
  email,
  firstname,
  lastname,
  middlename, // Kept for backward compatibility but not used
  toast,
  authType = 'signup', // Add authType parameter with default 'signup'
  preventNavigation = false // Prevent automatic navigation (useful for overlays)
) => {
  const navigate = useNavigate();
  const consumePostAuthRedirectPath = () => {
    try {
      const redirectPath = sessionStorage.getItem('postAuthRedirectPath');
      if (redirectPath && redirectPath.startsWith('/')) {
        sessionStorage.removeItem('postAuthRedirectPath');
        return redirectPath;
      }
    } catch (_) {
      // ignore session storage errors
    }
    return null;
  };
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [verified, setVerified] = useState(false);
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [otpError, setOtpError] = useState('');
  // Server-driven cooldown (see otp-auth-documentation.pdf)
  const [resendTimeout, setResendTimeout] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [currentOTP, setCurrentOTP] = useState('');
  const [otpFocusTrigger, setOtpFocusTrigger] = useState(0);
  const [previousValue, setPreviousValue] = useState(value);
  const [isResending, setIsResending] = useState(false); // Prevent race conditions
  const [resumeProcessed, setResumeProcessedState] = useState(null);
  const [profileIsProcessed, setProfileIsProcessed] = useState(null);
  const [resendCount, setResendCount] = useState(0);
  const [remainingVerifyAttempts, setRemainingVerifyAttempts] = useState(null);

  // Determine service based on type and authType
  const getService = () => {
    if (type === 'contact') {
      return authType === 'login' ? UserService : ResumeService;
    }
    // For email, always use UserService (both signup and linking use UserService)
    return UserService;
  };

  // Reset OTP state when contact/email value changes
  useEffect(() => {
    if (previousValue !== value && previousValue !== '') {
      setIsOTPSent(false);
      setShowOTPInput(false);
      setVerified(false);
      setCanResend(false);
      setResendTimeout(0);
      setOtpError('');
      setCurrentOTP('');
      setResendCount(0);
      setRemainingVerifyAttempts(null);
      setPreviousValue(value);
      return;
    }
    setPreviousValue(value);
  }, [value, type, authType]);

  // Timer effect - server-driven countdown
  useEffect(() => {
    let timer;
    let canResendDelayTimer;
    if (isOTPSent && resendTimeout > 0) {
      timer = setInterval(() => {
        setResendTimeout((prev) => Math.max(prev - 1, 0));
      }, 1000);
    }

    // Update canResend when timeout reaches 0
    if (resendTimeout === 0 && isOTPSent) {
      // Small UI delay to avoid abrupt button state changes
      canResendDelayTimer = setTimeout(() => {
        setCanResend(true);
      }, 300);
    } else {
      setCanResend(false);
    }

    return () => {
      if (timer) clearInterval(timer);
      if (canResendDelayTimer) clearTimeout(canResendDelayTimer);
    };
  }, [isOTPSent, resendTimeout]);

  const getUserCid = () => {
    try {
      const userData = getUser();
      return userData?.cid;
    } catch (error) {
      console.error('Error getting user id:', error);
      return null;
    }
  };

  const handleSendOTP = async (action = 'SEND') => {
    // Prevent race conditions - don't allow multiple simultaneous requests
    if (isOTPLoading || isResending) {
      return;
    }

    // For RESEND, check if cooldown is still active
    if (action === 'RESEND' && !canResend && resendTimeout > 0) {
      toast({
        message: `Wait ${resendTimeout}s to resend`,
        variant: 'default',
      });
      return;
    }

    try {
      setIsOTPLoading(true);
      setIsResending(true);
      const service = getService();

      let response;

      if (type === 'contact') {
        if (authType === 'login') {
          // Use login phone OTP endpoints
          const data = { phone: value };
          response = await (action === 'SEND'
            ? service.sendPhoneLoginOtp(data)
            : service.resendPhoneLoginOtp({
                phone: value,
                purpose: 'signin',
              }));
        } else {
          // Use signup phone OTP endpoints (existing behavior)
          const data = {
            phone: value,
            firstName: firstname,
            lastName: lastname,
          };
          const resendData = { phone: value, purpose: 'signup' };
          response = await (action === 'SEND'
            ? service.sendOtp(data)
            : service.resendOtp(resendData));
        }
      } else if (authType === 'link') {
        // Use email linking endpoints for secondary auth
        const data = { email: value };
        response = await (action === 'SEND'
          ? service.sendEmailLinkOtp(data)
          : service.resendEmailLinkOtp({ email: value }));
      } else {
        // Use regular email OTP endpoints (existing behavior)
        const data = {
          email: value,
          id: getUserCid(),
        };
        const resendData = { email, id: getUserCid() };
        response = await (action === 'SEND'
          ? service.sendOtp(data)
          : service.resendOtp(resendData));
      }

      if (response) {
        const otpMeta = response?.data || response;
        const otpStatus = otpMeta?.status;
        const timeoutSeconds = Number(otpMeta?.cooldown_seconds || 0);
        const newResendCount = Number(otpMeta?.resend_count || 0);
        const newRemainingAttempts =
          typeof otpMeta?.remaining_verify_attempts !== 'undefined'
            ? Number(otpMeta.remaining_verify_attempts)
            : null;

        setIsOTPSent(true);
        setShowOTPInput(true);
        setCurrentOTP('');
        setOtpFocusTrigger((prev) => prev + 1);
        setResendTimeout(timeoutSeconds);
        setOtpError(''); // Clear any previous errors
        setResendCount(newResendCount);
        setRemainingVerifyAttempts(newRemainingAttempts);

        const toastMessage = (() => {
          // Status values from otp-auth-documentation.pdf
          if (otpStatus === 'otp_already_active') {
            return timeoutSeconds > 0
              ? `OTP already active. Please wait ${timeoutSeconds}s.`
              : 'OTP already active.';
          }
          if (otpStatus === 'otp_sent') {
            return action === 'SEND' ? 'OTP sent!' : 'OTP resent!';
          }
          // Fallback if backend introduces a new status
          return action === 'SEND'
            ? 'OTP request processed.'
            : 'OTP resend processed.';
        })();

        toast({
          message: toastMessage,
          variant: otpStatus === 'otp_already_active' ? 'default' : 'success',
        });
      } else {
        throw new Error(response?.message || 'Failed to get OTP');
      }
    } catch (error) {
      console.error('OTP sending error:', error);

      const errorCode = error?.error_code;
      if (errorCode === 'OTP_COOLDOWN') {
        const retryAfterSeconds =
          Number(error?.retry_after) ||
          Number(error?.details?.retry_after_seconds) ||
          0;
        setIsOTPSent(true);
        setShowOTPInput(true);
        setResendTimeout(retryAfterSeconds);
        setOtpError(
          error?.message || 'Please wait before requesting another OTP.'
        );
        toast({
          message:
            error?.message || 'Please wait before requesting another OTP.',
          variant: 'default',
        });
        return;
      }

      // On error, maintain cooldown state if OTP was already sent before
      // Only reset state if this was the initial send attempt
      if (action === 'SEND' || !isOTPSent) {
        setIsOTPSent(false);
        setShowOTPInput(false);
        setCanResend(false);
        setResendTimeout(0);
        setResendCount(0);
        setRemainingVerifyAttempts(null);
      }
      // For RESEND errors, keep the cooldown active to prevent abuse

      toast({
        message: error?.message || 'Failed to get OTP. Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsOTPLoading(false);
      setIsResending(false);
    }
  };
  const processOtpVerifyResponse = async (responseData) => {
    // Handle different response structures for signup vs login
    // API response structure: { success, message, data: { tokens, resume_processed } }
    // Or direct structure: { tokens, resume_processed }
    if (!responseData) {
      throw new Error('Empty response received');
    }
    const apiData = responseData.data || responseData;
    const { tokens } = apiData;
    const { resume_processed } = apiData;

    if (tokens?.access_token) {
      // Store tokens in localStorage (shared across tabs)
      setToken(tokens.access_token);
      if (tokens.refresh_token) {
        setRefreshToken(tokens.refresh_token);
      }

      // Extract and store resume_processed flag from response (for signup flow)
      if (authType === 'signUp' && typeof resume_processed !== 'undefined') {
        setResumeProcessedState(resume_processed);
      }

      let decodedUser;
      try {
        decodedUser = jwtDecode(tokens.access_token);
      } catch (error) {
        console.error('Failed to decode access token (OTP flow):', error);
        throw new Error('Invalid token received');
      }

      // Format user data from token claims
      const userData = {
        cid: decodedUser?.cid,
        email: decodedUser?.email,
        firstName: decodedUser?.given_name,
        lastName: decodedUser?.family_name,
        emailVerified: decodedUser?.email_verified,
        roles: decodedUser?.realm_access?.roles,
        ack: decodedUser?.ack,
        secAuth: decodedUser?.secAuth,
      };

      // Store user data in localStorage
      try {
        setUser(userData);
      } catch (storageError) {
        console.error('Failed to persist user to localStorage:', storageError);
      }

      // Navigate based on authType (skip if preventNavigation is true)
      if (!preventNavigation) {
        if (authType === 'login') {
          try {
            const candidateId = userData?.cid;
            const profile = await ResumeService.getProfile(candidateId);
            const resumeProcessed =
              profile?.profile_data?.other_details?.resume_processed;
            if (typeof resumeProcessed !== 'undefined') {
              setResumeProcessedState(resumeProcessed);
            }
          } catch (e) {
            console.error('Failed to fetch profile processing status:', e);
          }
          // If email is not verified, redirect to create-link to complete account setup
          if (userData?.secAuth === 'none') {
            navigate('/create-link', { replace: true });
            return responseData;
          }
          const postAuthRedirectPath = consumePostAuthRedirectPath();
          if (postAuthRedirectPath) {
            navigate(postAuthRedirectPath, { replace: true });
            return responseData;
          }
          // Navigate to profile dashboard after login
          navigate('/profile-dashboard');
        } else if (authType === 'link') {
          // For email linking, do not navigate here. Let the UI decide next step.
        } else if (authType === 'signUp') {
          // UploadResume signup flow: stay on the same page (no navigation)
          // Consumers of the hook will drive the next-step UI
        } else {
          // For signup, navigate to create account
          navigate('/create-link');
        }
      } else if (authType === 'login') {
        // When preventNavigation is true, still fetch profile data but don't navigate
        try {
          const candidateId = userData?.cid;
          const profile = await ResumeService.getProfile(candidateId);
          const resumeProcessed =
            profile?.profile_data?.other_details?.resume_processed;
          if (typeof resumeProcessed !== 'undefined') {
            setResumeProcessedState(resumeProcessed);
          }
        } catch (e) {
          console.error('Failed to fetch profile processing status:', e);
        }
      } else if (authType === 'signUp') {
        // When preventNavigation is true, still fetch profile data but don't navigate
        try {
          const candidateId = userData?.cid;
          if (candidateId) {
            const profile = await ResumeService.getProfile(candidateId);
            const isProcessed =
              profile?.profile_data?.other_details?.is_processed;
            const resumeProcessed =
              profile?.profile_data?.other_details?.resume_processed;
            if (typeof isProcessed !== 'undefined') {
              setProfileIsProcessed(isProcessed);
            }
            if (typeof resumeProcessed !== 'undefined') {
              setResumeProcessedState(resumeProcessed);
            }
          }
        } catch (e) {
          console.error('Failed to fetch profile processing status:', e);
        }
      }

      return responseData;
    }

    throw new Error('Invalid token received');
  };
  const handleVerifyOTP = async (otp, additionalData = {}) => {
    // Store the current OTP value
    setCurrentOTP(otp);

    // Clear error when user starts typing (but not when clearing)
    if (otp.length > 0 && otpError) {
      setOtpError('');
    }

    // For email linking, don't auto-verify on 4 digits - wait for explicit call
    const shouldAutoVerify = authType !== 'link' && otp.length === 4;
    const isExplicitCall = Object.keys(additionalData).length > 0;

    // Only proceed if we have a valid OTP and should verify
    if ((shouldAutoVerify || isExplicitCall) && otp.length === 4) {
      try {
        setIsVerifyingOTP(true);
        setOtpError('');
        const service = getService();

        let response;

        if (type === 'contact') {
          if (authType === 'login') {
            // Use login phone OTP verification endpoint
            const data = { phone: value, otp, purpose: 'signin' };
            response = await service.verifyPhoneLoginOtp(data);
          } else {
            // Use signup phone OTP verification endpoint
            const data = { phone: value, otp, purpose: 'signup' };
            response = await service.verifyOtp(data);
          }
        } else if (authType === 'link') {
          // Use email linking verification endpoint with password
          const data = {
            email: value,
            otp,
            password: additionalData.password,
            confirmPassword: additionalData.confirmPassword,
          };
          response = await service.verifyEmailLinkOtp(data);
        } else {
          // Use regular email OTP verification (existing behavior)
          const data = { email: value, otp, id: getUserCid() };
          response = await service.verifyOtp(data);
        }

        if (response) {
          // For email linking we do not receive tokens and should not process login
          if (authType !== 'link') {
            processOtpVerifyResponse(response);
          }
          setVerified(true);
          setShowOTPInput(false);
          setResendTimeout(0);
          setCanResend(false);
          setResendCount(0);
          setRemainingVerifyAttempts(null);

          toast({
            message: `Your ${type} has been verified successfully.`,
            variant: 'success',
          });
        } else {
          throw new Error(response.message || 'Invalid OTP');
        }
      } catch (error) {
        console.error('OTP verification error:', error);
        setVerified(false);
        const errorCode = error?.error_code;
        if (errorCode === 'OTP_INCORRECT') {
          const attemptsLeft =
            error?.details?.remaining_verify_attempts ??
            error?.remaining_verify_attempts;
          if (typeof attemptsLeft !== 'undefined') {
            setRemainingVerifyAttempts(Number(attemptsLeft));
          }
          setOtpError(
            typeof attemptsLeft !== 'undefined'
              ? `Wrong OTP. ${attemptsLeft} attempts left.`
              : 'Wrong OTP. Please try again.'
          );
        } else if (errorCode === 'OTP_MAX_ATTEMPTS') {
          setIsOTPSent(false);
          setShowOTPInput(false);
          setResendTimeout(0);
          setCanResend(false);
          setResendCount(0);
          setRemainingVerifyAttempts(null);
          setOtpError(
            error?.message || 'Too many attempts. Please request a new OTP.'
          );
        } else if (errorCode === 'OTP_NOT_FOUND') {
          setIsOTPSent(false);
          setShowOTPInput(false);
          setResendTimeout(0);
          setCanResend(false);
          setResendCount(0);
          setRemainingVerifyAttempts(null);
          setOtpError(
            error?.message || 'OTP expired. Please request a new OTP.'
          );
        } else if (errorCode === 'OTP_ALREADY_USED') {
          setOtpError(
            error?.message || 'OTP already used. Please request a new OTP.'
          );
        } else {
          setOtpError(
            error?.message || 'Failed to verify OTP. Please try again.'
          );
        }
        toast({
          message: error?.message || 'Invalid OTP. Please try again',
          variant: 'destructive',
        });
      } finally {
        setIsVerifyingOTP(false);
      }
    }
  };

  // Method to manually verify OTP with additional data (like password)
  const verifyOTPWithData = async (additionalData = {}) => {
    if (currentOTP.length === 4) {
      await handleVerifyOTP(currentOTP, additionalData);
    } else {
      setOtpError('Please enter a valid 4-digit OTP');
    }
  };

  return {
    showOTPInput,
    verified,
    isOTPSent,
    isOTPLoading,
    isVerifyingOTP,
    otpError,
    resendTimeout,
    canResend,
    currentOTP,
    otpFocusTrigger,
    resumeProcessed,
    profileIsProcessed,
    resendCount,
    remainingVerifyAttempts,
    handleSendOTP,
    handleVerifyOTP,
    verifyOTPWithData,
  };
};
